import { PaginationType, Table } from '@octano/global-ui';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Col, Container, Row } from 'reactstrap';
import TableEmptyContent from '../../../components/Text/TableEmptyContent';
import { TranslationsKeys } from '../../../locales/translations';
import { useClassesBookColumns } from '../hooks/useClassesBookColumns';
import SearchControls from './SearchControls';
import {
  ITEMS_PER_PAGE_DEFAULT,
  useClassesBookLoader,
} from '../provider/ClassesBookLoader';
import { SearchParams } from '../types';

export interface HandleSearch {
  searchParams: SearchParams<string>;
  page: number;
}

const ClassesBookList = () => {
  const { t } = useTranslation(TranslationsKeys.CLASSES_BOOK_LIST);
  const { data, loading, query, updateQuery } = useClassesBookLoader();

  const [filters, setFilters] = useState<SearchParams<string>>();
  const columns = useClassesBookColumns({
    onDownloadReport: (url?: string) => {
      if (!url?.trim()) {
        return;
      }
      window.open(url?.trim(), '_blank');
    },
  });

  const results = useMemo(() => {
    return data?.data ?? [];
  }, [data]);

  const paginationTable = useMemo<PaginationType | undefined>(() => {
    if (data) {
      return {
        totalItems: data.total,
        onChangePage: (page) => updateQuery({ ...query, page }),
        itemsPerPage: ITEMS_PER_PAGE_DEFAULT,
        totalPages: data.total_pages,
        currentPage: query.page,
      };
    } else return undefined;
  }, [data, query, updateQuery]);

  const isSearch = useMemo(() => {
    return filters?.hasFilters ?? false;
  }, [filters]);

  const handleSearch = useCallback(
    async ({ searchParams, page = 1 }: HandleSearch) => {
      updateQuery({
        page: page,
        ...searchParams,
      });
    },
    [updateQuery],
  );

  return (
    <>
      <Card className="m-3 py-4">
        <Container fluid>
          <Row>
            <Col xs={12}>
              <p className="text-light fs-14 mb-5">{t(`description`)}</p>
            </Col>
          </Row>

          <SearchControls onSearch={handleSearch} onFilter={setFilters} />

          <Row className="mt-3">
            <Col xs={12}>
              <Table
                columns={columns}
                data={results}
                isLoadingResults={loading}
                pagination={paginationTable}
                noResultsText={
                  <TableEmptyContent
                    title={t(
                      isSearch
                        ? 'errors.noResultsSearchTitle'
                        : 'errors.noResultsTitle',
                    )}
                    subtitle={t(
                      isSearch
                        ? 'errors.noResultsSearchBody'
                        : 'errors.noResultsBody',
                    )}
                  />
                }
              />
            </Col>
          </Row>
        </Container>
      </Card>
    </>
  );
};

export default ClassesBookList;
